import { LocationRegisterList } from 'src/app/_data/location-resgister';

function toNumber(value: any): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function roundCurrency(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function normalizeName(value: any): string {
  return String(value || '').trim().toLowerCase();
}

function readLineAmount(line: any): number {
  return toNumber(line?.subTotalCost ?? line?.SubTotalCost ?? line?.totalPrice ?? line?.TotalPrice);
}

function isActive(value: any): boolean {
  return value?.isActive ?? value?.IsActive ?? true;
}

const REGISTER_FEATURE_TYPE = 6; // AdditionalRegister
const LOCATION_FEATURE_TYPE = 7; // AdditionalLocationPlus1Reg
// Same LocationRegisterList catalog ids BillingAndPlansComponent's registerExtraPrice/
// locationExtraPrice already key off of (id 1 = Registers, id 2 = Locations) — the backend uses
// these as the SubscriptionPlanPkg packageId for register/location extra purchases.
const REGISTER_PACKAGE_ID = '1';
const LOCATION_PACKAGE_ID = '2';
// Same fallback values BillingAndPlansComponent's registerExtraPrice/locationExtraPrice use.
const REGISTER_UNIT_PRICE = LocationRegisterList.find((i) => i.id === 1)?.price ?? 49;
const LOCATION_UNIT_PRICE = LocationRegisterList.find((i) => i.id === 2)?.price ?? 79;

function packageLineById(subscription: any, packageId: string): any | null {
  const purchasedLines: any[] = subscription?.subscriptionPlanPkg ?? subscription?.SubscriptionPlanPkg ?? [];
  return purchasedLines.find((line: any) => String(line?.packageId ?? line?.PackageId ?? '') === packageId && !!isActive(line)) || null;
}

/** AdditionalRegister/AdditionalLocationPlus1Reg SubscribedFeatures rows can come back from the
 *  backend with PricePerPackage/SubTotalCost zeroed out even though Qty is billed — the real
 *  dollar amount in that case lives on the matching SubscriptionPlanPkg row instead. Without this
 *  fallback, extra registers/locations silently drop out of the recurring total even though the
 *  "Locations & registers" usage card (which reads pricing from a different source) still shows
 *  them as billed extras. */
function readFeatureAmount(subscription: any, feature: any): number {
  const amount = readLineAmount(feature);
  if (amount > 0) return amount;

  const qty = toNumber(feature?.qty ?? feature?.Qty);
  if (qty <= 0) return amount;

  const type = toNumber(feature?.type ?? feature?.Type);
  const fallbackPackageId =
    type === REGISTER_FEATURE_TYPE ? REGISTER_PACKAGE_ID :
    type === LOCATION_FEATURE_TYPE ? LOCATION_PACKAGE_ID :
    null;
  if (!fallbackPackageId) return amount;

  const line = packageLineById(subscription, fallbackPackageId);
  const lineAmount = line ? readLineAmount(line) : 0;
  if (lineAmount > 0) return lineAmount;

  // The SubscriptionPlanPkg row itself can also come back zeroed (confirmed on a live account:
  // the location package line had subTotalCost/totalPrice = 0 despite qty being billed) — without
  // this, the recurring total silently drops the extra-location/register charge instead of using
  // the real catalog rate, understating the hero total (e.g. $133 shown instead of the real $212).
  const catalogPrice = type === REGISTER_FEATURE_TYPE ? REGISTER_UNIT_PRICE : LOCATION_UNIT_PRICE;
  return roundCurrency(catalogPrice * qty);
}

function activeSubscribedFeatures(subscription: any): any[] {
  const features: any[] = subscription?.subscribedFeatures ?? subscription?.SubscribedFeatures ?? [];
  return features.filter((feature: any) => !!isActive(feature));
}

function matchingPackageLine(subscription: any, addon: any): any | null {
  const addonId = addon?.id != null ? String(addon.id) : null;
  if (!addonId) return null;

  const purchasedLines: any[] = subscription?.subscriptionPlanPkg ?? subscription?.SubscriptionPlanPkg ?? [];
  return purchasedLines.find((line: any) => {
    const linePackageId = String(line?.packageId ?? line?.PackageId ?? '');
    return linePackageId === addonId && !!isActive(line) && readLineAmount(line) > 0;
  }) || null;
}

export function isSubscriptionAddonActive(subscription: any, addon: any): boolean {
  const name = normalizeName(addon?.planName || addon?.title);
  const activeInFeatures = activeSubscribedFeatures(subscription).some((f: any) => {
    const title = normalizeName(f?.title ?? f?.Title);
    return title === name;
  });

  if (activeInFeatures) return true;
  return !!matchingPackageLine(subscription, addon);
}

export function calculateSubscriptionRecurringSubtotal(subscription: any, addons: any[]): number {
  const planCost = toNumber(subscription?.planCost ?? subscription?.PlanCost);
  const features = activeSubscribedFeatures(subscription);
  const featureSubtotal = features.reduce((sum: number, feature: any) => sum + readFeatureAmount(subscription, feature), 0);
  const activeAddonFeatureNames = new Set(
    features
      .filter((feature: any) => !!(feature?.isAddon ?? feature?.IsAddon))
      .map((feature: any) => normalizeName(feature?.title ?? feature?.Title))
      .filter((name: string) => !!name)
  );

  const packageAddonSubtotal = (addons || []).reduce((sum: number, addon: any) => {
    const addonName = normalizeName(addon?.planName || addon?.title);
    if (activeAddonFeatureNames.has(addonName)) return sum;

    const line = matchingPackageLine(subscription, addon);
    return line ? sum + readLineAmount(line) : sum;
  }, 0);

  return roundCurrency(planCost + featureSubtotal + packageAddonSubtotal);
}

// Taxed the same as every other line, plan fee included — confirmed against the account owner's
// expected invoice total: $35 plan + $79 locations + $49 registers + $49 add-on = $212, taxed as a
// whole at 8.25% = $17.49 (not $14.60, which is what carving the plan fee out of the taxed base
// produced). Matches PaymentPageComponent.submitOrder()'s per-line taxAmount on the plan's own
// subscriptionPlanPkg entry.
export function calculateSubscriptionRecurringTaxableSubtotal(subscription: any, addons: any[]): number {
  const planCost = toNumber(subscription?.planCost ?? subscription?.PlanCost);
  const features = activeSubscribedFeatures(subscription);
  const featureSubtotal = features.reduce((sum: number, feature: any) => sum + readFeatureAmount(subscription, feature), 0);
  const activeAddonFeatureNames = new Set(
    features
      .filter((feature: any) => !!(feature?.isAddon ?? feature?.IsAddon))
      .map((feature: any) => normalizeName(feature?.title ?? feature?.Title))
      .filter((name: string) => !!name)
  );

  const packageAddonSubtotal = (addons || []).reduce((sum: number, addon: any) => {
    const addonName = normalizeName(addon?.planName || addon?.title);
    if (activeAddonFeatureNames.has(addonName)) return sum;

    const line = matchingPackageLine(subscription, addon);
    return line ? sum + readLineAmount(line) : sum;
  }, 0);

  return roundCurrency(planCost + featureSubtotal + packageAddonSubtotal);
}

export function calculateSubscriptionRecurringTotal(subscription: any, addons: any[], taxRate: number): number {
  const subtotal = calculateSubscriptionRecurringSubtotal(subscription, addons);
  const taxableSubtotal = calculateSubscriptionRecurringTaxableSubtotal(subscription, addons);
  const tax = roundCurrency((taxableSubtotal * toNumber(taxRate)) / 100);
  return roundCurrency(subtotal + tax);
}

export function calculateSubscriptionRecurringTaxAmount(subscription: any, addons: any[], taxRate: number): number {
  const taxableSubtotal = calculateSubscriptionRecurringTaxableSubtotal(subscription, addons);
  return roundCurrency((taxableSubtotal * toNumber(taxRate)) / 100);
}

export function readUpcomingInvoiceTaxAmount(invoice: any): number {
  return toNumber(invoice?.totalTaxAmount ?? invoice?.TotalTaxAmount);
}

export function readUpcomingInvoiceAmount(invoice: any): number {
  const grandTotal = toNumber(invoice?.grandTotal ?? invoice?.GrandTotal);
  if (grandTotal > 0) return grandTotal;

  const nextChargeAmount = toNumber(invoice?.nextChargeAmount ?? invoice?.NextChargeAmount);
  if (nextChargeAmount > 0) return nextChargeAmount;

  const subtotal = toNumber(invoice?.invoiceSubtotal ?? invoice?.InvoiceSubtotal);
  const tax = toNumber(invoice?.totalTaxAmount ?? invoice?.TotalTaxAmount);
  if (subtotal > 0 || tax > 0) return roundCurrency(subtotal + tax);

  return 0;
}
